import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { tr } from "@/lib/i18n";
import { FileBarChart, Play, Download, FileDown, Save, Trash2, Filter, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["Submitted", "In Review", "Pending", "On Hold", "Approved", "Rejected"];
const SORTS = ["number", "date", "type", "status"];
const SORT_LABELS = { number: "File number", date: "Date", type: "Type", status: "Status" };
const EMPTY_FILTERS = { from_date: "", to_date: "", ward: "", planner: "", app_type: "", status: "", category: "", applicant: "", sort: "number" };

export default function Reports() {
    useLanguage();
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState({ builtin: [], custom: [] });
    const [appTypes, setAppTypes] = useState([]);
    const [selected, setSelected] = useState("application_summary");
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [saveOpen, setSaveOpen] = useState(false);
    const [saveName, setSaveName] = useState("");

    const loadCatalog = useCallback(() => {
        api.get("/reports/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    }, []);

    useEffect(() => {
        loadCatalog();
        api.get("/config/app-types").then(({ data }) => setAppTypes(data)).catch(() => {});
    }, [loadCatalog]);

    const payload = () => {
        const p = { report_type: selected, sort: filters.sort };
        for (const k of ["from_date", "to_date", "ward", "planner", "app_type", "status", "category", "applicant"]) {
            if (filters[k]) p[k] = filters[k];
        }
        return p;
    };

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/reports/run", payload());
            setResult(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const downloadBlob = async (url, name, type, open) => {
        try {
            const res = await api.post(url, payload(), { responseType: "blob" });
            const blobUrl = URL.createObjectURL(new Blob([res.data], { type }));
            if (open) {
                window.open(blobUrl, "_blank");
            } else {
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = name;
                a.click();
                URL.revokeObjectURL(blobUrl);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const saveCustom = async () => {
        try {
            await api.post("/reports/custom", { name: saveName, report_type: selected, filters: payload() });
            toast.success(tr("Report saved"));
            setSaveOpen(false);
            setSaveName("");
            loadCatalog();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const loadCustom = (c) => {
        setSelected(c.report_type);
        setFilters({ ...EMPTY_FILTERS, ...c.filters });
        setResult(null);
    };

    const deleteCustom = async (c) => {
        await api.delete(`/reports/custom/${c.id}`).catch(() => {});
        loadCatalog();
    };

    const activeDef = catalog.builtin.find((r) => r.id === selected);
    const inp = "w-full px-3 py-2 rounded-[10px] border border-slate-200 dark:border-white/10 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]/40";

    return (
        <div className="space-y-6" data-testid="reports-page">
            <div>
                <h1 className="font-heading text-3xl font-bold">{tr("Reports")}</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{tr("Every report runs on live data — filter at runtime, drill into the files, export to CSV or a branded PDF, and save your own report definitions.")}</p>
            </div>

            <div className="grid lg:grid-cols-12 gap-6">
                {/* Catalog */}
                <div className="lg:col-span-3 space-y-2" data-testid="reports-catalog">
                    {catalog.builtin.map((r) => (
                        <button key={r.id} data-testid={`report-item-${r.id}`} onClick={() => { setSelected(r.id); setResult(null); }}
                            className={`w-full text-left p-3.5 rounded-[14px] border transition-all ${selected === r.id ? "border-[#00B4D8] bg-[#00B4D8]/5 shadow-sm" : "border-slate-200 dark:border-white/10 bg-card hover:border-slate-300"}`}>
                            <div className="flex items-center gap-2.5">
                                <FileBarChart className={`w-4 h-4 shrink-0 ${selected === r.id ? "text-[#0092B0]" : "text-slate-400"}`} />
                                <div className="text-sm font-bold">{tr(r.name)}</div>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-1 ml-6">{tr(r.desc)}</div>
                        </button>
                    ))}
                    {catalog.custom.length > 0 && (
                        <div className="pt-3 mt-3 border-t border-slate-200 dark:border-white/10">
                            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">{tr("My saved reports")}</div>
                            {catalog.custom.map((c) => (
                                <div key={c.id} className="flex items-center gap-2 p-2.5 rounded-[10px] hover:bg-slate-50 dark:hover:bg-white/5 group" data-testid={`custom-report-${c.id.slice(0, 8)}`}>
                                    <button className="flex-1 text-left text-sm font-semibold text-[#0092B0]" onClick={() => loadCustom(c)}>{c.name}</button>
                                    <button data-testid={`custom-report-delete-${c.id.slice(0, 8)}`} onClick={() => deleteCustom(c)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-[#EF476F] transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Filters + results */}
                <div className="lg:col-span-9 space-y-5">
                    <div className="bg-card rounded-[18px] border border-slate-200/70 dark:border-white/10 p-5" data-testid="reports-filters">
                        <div className="flex items-center gap-2 mb-4">
                            <Filter className="w-4 h-4 text-[#0092B0]" />
                            <span className="text-xs uppercase tracking-[0.14em] font-bold text-slate-400">{tr("Runtime filters")}</span>
                            <span className="text-xs text-slate-400 ml-1">— {activeDef ? tr(activeDef.name) : ""}</span>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">{tr("From")}</label>
                                <input type="date" data-testid="filter-from-date" className={inp} value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">{tr("To")}</label>
                                <input type="date" data-testid="filter-to-date" className={inp} value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">{tr("Ward")}</label>
                                <input data-testid="filter-ward" className={inp} placeholder={tr("e.g. Ward 4")} value={filters.ward} onChange={(e) => setFilters({ ...filters, ward: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">{tr("Planner")}</label>
                                <input data-testid="filter-planner" className={inp} placeholder={tr("Planner name")} value={filters.planner} onChange={(e) => setFilters({ ...filters, planner: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">{tr("Application type")}</label>
                                <select data-testid="filter-app-type" className={inp} value={filters.app_type} onChange={(e) => setFilters({ ...filters, app_type: e.target.value })}>
                                    <option value="">{tr("All types")}</option>
                                    {appTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">{tr("Status")}</label>
                                <select data-testid="filter-status" className={inp} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                                    <option value="">{tr("All")}</option>
                                    {STATUSES.map((s) => <option key={s} value={s}>{tr(s)}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">{tr("Applicant")}</label>
                                <input data-testid="filter-applicant" className={inp} placeholder={tr("Applicant name")} value={filters.applicant} onChange={(e) => setFilters({ ...filters, applicant: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">{tr("Sort by")}</label>
                                <select data-testid="filter-sort" className={inp} value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })}>
                                    {SORTS.map((s) => <option key={s} value={s}>{tr(SORT_LABELS[s])}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2.5 mt-4">
                            <button data-testid="report-run-btn" onClick={run} disabled={busy}
                                className="brand-btn inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] text-sm font-bold disabled:opacity-50">
                                <Play className="w-4 h-4" /> {busy ? tr("Running…") : tr("Run report")}
                            </button>
                            <button data-testid="report-export-csv-btn" onClick={() => downloadBlob("/reports/export.csv", `${selected}.csv`, "text/csv", false)} disabled={!result}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-sm font-bold border border-slate-300 dark:border-white/15 hover:border-[#00B4D8] hover:text-[#0092B0] transition-colors disabled:opacity-40">
                                <Download className="w-4 h-4" /> {tr("Export CSV")}
                            </button>
                            <button data-testid="report-export-pdf-btn" onClick={() => downloadBlob("/reports/report.pdf", `${selected}.pdf`, "application/pdf", true)} disabled={!result}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-sm font-bold border border-slate-300 dark:border-white/15 hover:border-[#00B4D8] hover:text-[#0092B0] transition-colors disabled:opacity-40">
                                <FileDown className="w-4 h-4" /> {tr("Download PDF")}
                            </button>
                            <button data-testid="report-save-btn" onClick={() => setSaveOpen(true)} disabled={!result}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-sm font-bold border border-slate-300 dark:border-white/15 hover:border-[#00B4D8] hover:text-[#0092B0] transition-colors disabled:opacity-40">
                                <Save className="w-4 h-4" /> {tr("Save as my report")}
                            </button>
                        </div>
                    </div>

                    {result && (
                        <div className="bg-card rounded-[18px] border border-slate-200/70 dark:border-white/10 overflow-hidden" data-testid="report-results">
                            <div className="px-5 py-3 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-[0.12em]" data-testid="report-record-count">{result.count} {tr("records")}</span>
                                <span className="text-[11px] text-slate-400">{tr("Click a file number to drill into the record")}</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="report-results-table">
                                    <thead>
                                        <tr className="border-b border-slate-100 dark:border-white/5 bg-slate-50/60 dark:bg-white/[0.02]">
                                            {result.columns.map((c) => <th key={c} className="text-left text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 px-4 py-2.5 whitespace-nowrap">{tr(c)}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.rows.map((row, i) => (
                                            <tr key={i} className="border-b border-slate-50 dark:border-white/5 hover:bg-slate-50/60 dark:hover:bg-white/[0.03]">
                                                {row.map((cell, j) => (
                                                    <td key={j} className={`px-4 py-2.5 whitespace-nowrap ${j === 0 ? "font-bold" : "text-slate-500 dark:text-slate-300"}`}>
                                                        {j === 0 && String(cell).includes("-") ? (
                                                            <button className="inline-flex items-center gap-1 text-[#0092B0] hover:underline" data-testid={`report-drill-${i}`}
                                                                onClick={() => navigate(`/applications/${encodeURIComponent(cell)}`)}>
                                                                {cell} <ExternalLink className="w-3 h-3" />
                                                            </button>
                                                        ) : String(cell)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
                <DialogContent className="sm:max-w-md" data-testid="report-save-dialog">
                    <DialogHeader><DialogTitle>{tr("Save as my report")}</DialogTitle></DialogHeader>
                    <p className="text-xs text-slate-400">{tr("Saves this report type and the current filters under your name for one-click reuse.")}</p>
                    <input data-testid="report-save-name" className={inp} placeholder={tr("Report name (e.g. Ward 4 active files)")} value={saveName} onChange={(e) => setSaveName(e.target.value)} />
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setSaveOpen(false)} className="px-4 py-2 rounded-[10px] text-sm font-bold text-slate-500">{tr("Cancel")}</button>
                        <button data-testid="report-save-confirm-btn" onClick={saveCustom} disabled={!saveName.trim()}
                            className="brand-btn px-4 py-2 rounded-[10px] text-sm font-bold disabled:opacity-50">{tr("Save report")}</button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
